"use client";

import React from "react";
import { trackDetailsDrawer } from "@/lib/analytics";
import { SignalDetailDrawer } from "@/components/DivWrapperScreen/drawers/SignalDetailDrawer";
import { AlignDetailDrawer } from "@/components/DivWrapperScreen/drawers/AlignDetailDrawer";
import { SuggestionDetailDrawer } from "@/components/DivWrapperScreen/drawers/SuggestionDetailDrawer";
import { MeetingDetailDrawer } from "@/components/DivWrapperScreen/drawers/MeetingDetailDrawer";
import { ScoringDetailsDrawer } from "@/components/call-history/ScoringDetailsDrawer";
import { EmailDraftDrawer } from "@/components/call-history/EmailDraftDrawer";
import { DealMomentumDrawer } from "@/components/call-history/DealMomentumDrawer";
import { ManagerCoachingEmailDrawer } from "@/components/manager-dashboard/ManagerCoachingEmailDrawer";

export type DrawerType =
  | "signal"
  | "meeting"
  | "engagement"
  | "alignment"
  | "suggestion"
  | "scoring"
  | "email"
  | "momentum"
  | null;

export interface DrawerData {
  type: DrawerType;
  signalId?: number;
  meetingId?: number;
  engagementId?: number;
  alignmentId?: string | number;
  suggestionId?: string | number;
  suggestionIconType?: "target" | "sparkles";
  scoringAccountId?: number;
  scoringCallRowId?: string;
  scrollToSection?: string; // Section ID to scroll to when drawer opens
  accountId?: number;
  repEmail?: string;
  [key: string]: any; // Allow additional data for future drawer types
}

interface DrawerContextType {
  isOpen: boolean;
  drawerData: DrawerData | null;
  openDrawer: (type: DrawerType, data?: Partial<DrawerData>) => void;
  closeDrawer: () => void;
}

const DrawerContext = React.createContext<DrawerContextType | undefined>(undefined);

export const useDrawer = () => {
  const context = React.useContext(DrawerContext);
  if (!context) {
    throw new Error("useDrawer must be used within a DrawerProvider");
  }
  return context;
};

interface DrawerProviderProps {
  children: React.ReactNode;
}

export const DrawerProvider: React.FC<DrawerProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [drawerData, setDrawerData] = React.useState<DrawerData | null>(null);

  const openDrawer = React.useCallback((type: DrawerType, data?: Partial<DrawerData>) => {
    if (!type) {
      console.warn("Drawer type is required");
      return;
    }

    if (
      type === "signal" ||
      type === "alignment" ||
      type === "suggestion" ||
      type === "meeting" ||
      type === "engagement"
    ) {
      trackDetailsDrawer(type, {
        suggestion_id: data?.suggestionId,
        signal_id: data?.signalId,
        alignment_id: data?.alignmentId,
      });
    }

    setDrawerData({
      type,
      ...data,
    } as DrawerData);
    setIsOpen(true);
  }, []);

  const closeDrawer = React.useCallback(() => {
    setIsOpen(false);
    // Clear data after animation completes
    setTimeout(() => {
      setDrawerData(null);
    }, 300);
  }, []);

  const value = React.useMemo(
    () => ({
      isOpen,
      drawerData,
      openDrawer,
      closeDrawer,
    }),
    [isOpen, drawerData, openDrawer, closeDrawer]
  );

  return (
    <DrawerContext.Provider value={value}>
      {children}
      <GlobalDrawer />
    </DrawerContext.Provider>
  );
};

/**
 * Global drawer component that renders different drawer types based on drawerData.type
 */
const GlobalDrawer: React.FC = () => {
  const { isOpen, drawerData, closeDrawer } = useDrawer();

  if (!drawerData || !isOpen) return null;

  switch (drawerData.type) {
    case "signal":
      return (
        <SignalDetailDrawer
          signalId={drawerData.signalId || null}
          open={isOpen}
          onOpenChange={(open) => {
            if (!open) closeDrawer();
          }}
        />
      );

    case "alignment":
      return (
        <AlignDetailDrawer
          alignmentId={drawerData.alignmentId || null}
          open={isOpen}
          onOpenChange={(open) => {
            if (!open) closeDrawer();
          }}
        />
      );

    case "suggestion":
      return (
        <SuggestionDetailDrawer
          suggestionId={drawerData.suggestionId || null}
          iconType={drawerData.suggestionIconType}
          open={isOpen}
          onOpenChange={(open) => {
            if (!open) closeDrawer();
          }}
        />
      );

    case "meeting":
      return (
        <MeetingDetailDrawer
          meetingId={drawerData.meetingId || null}
          scrollToSection={drawerData.scrollToSection}
          open={isOpen}
          onOpenChange={(open) => {
            if (!open) closeDrawer();
          }}
        />
      );

    case "engagement":
      // TODO: Implement engagement drawer when needed
      // Example: return <EngagementDetailDrawer engagementId={drawerData.engagementId} open={isOpen} onOpenChange={...} />;
      return null;

    case "scoring": {
      const { meetingId } = drawerData;
      const parsedMeetingId = Number(meetingId);
      if (!meetingId || Number.isNaN(parsedMeetingId)) return null;

      return (
        <ScoringDetailsDrawer
          open={isOpen}
          onOpenChange={(open) => {
            if (!open) closeDrawer();
          }}
          meetingId={parsedMeetingId}
        />
      );
    }

    case "email": {
      const trimmedRepEmail = drawerData.repEmail?.trim();
      if (trimmedRepEmail) {
        return (
          <ManagerCoachingEmailDrawer
            open={isOpen}
            onOpenChange={(open) => {
              if (!open) closeDrawer();
            }}
            repEmail={trimmedRepEmail}
            accountId={drawerData.accountId}
          />
        );
      }

      const { meetingId } = drawerData;
      const parsedMeetingId = Number(meetingId);
      if (!meetingId || Number.isNaN(parsedMeetingId)) return null;

      return (
        <EmailDraftDrawer
          open={isOpen}
          onOpenChange={(open) => {
            if (!open) closeDrawer();
          }}
          meetingId={parsedMeetingId}
        />
      );
    }

    case "momentum": {
      const parsedMeetingId = Number(drawerData.meetingId);
      if (!drawerData.meetingId || Number.isNaN(parsedMeetingId) || parsedMeetingId <= 0) {
        return null;
      }

      return (
        <DealMomentumDrawer
          open={isOpen}
          onOpenChange={(open: boolean) => {
            if (!open) closeDrawer();
          }}
          meetingId={parsedMeetingId}
        />
      );
    }

    default:
      return null;
  }
};

